import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Eraser,
  AlertTriangle,
  Loader2,
  CheckCircle,
  LogIn,
  Send,
  Mail,
  Database,
} from 'lucide-react';
import { clearUserData } from '@/lib/companyDeletion';
import { toast } from 'sonner';

export default function DeleteDataPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Logged-in user state
  const [understood, setUnderstood] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  
  // Form state for users without access
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formReason, setFormReason] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleDeleteData = async () => {
    if (!user) return;

    if (!understood) {
      toast.warning('Você precisa confirmar que entendeu as consequências');
      return;
    }

    setDeleting(true);

    try {
      // UMA TRANSAÇÃO, NO SERVIDOR (ver `src/lib/companyDeletion.ts`).
      //
      // Antes eram cinco deletes em sequência aqui: orçamentos → clientes →
      // catálogo → empresas → notificações. O quarto falhava com 23502
      // (`company_id` era NOT NULL e a FK era SET NULL), e a essa altura os três
      // primeiros já tinham sido destruídos — meio-estado irreversível, com
      // "Erro ao limpar dados. Tente novamente." que nunca resolveria. Medido em
      // aparelho: quotes 1→0, clients 1→0, products 1→0, companies 1→1.
      //
      // O `if (error) throw` de cada passo (a correção do D6) FUNCIONAVA: ele
      // capturou a falha e avisou. Guard de erro não substitui ORDEM — ele
      // transforma corrupção silenciosa em corrupção anunciada.
      //
      // De quebra, o financeiro agora some junto: `finance_entries` nunca era
      // apagado aqui e sobrevivia a uma tela que promete apagar "todos os seus
      // dados".
      await clearUserData();

      setDeleted(true);
      toast.success('Dados limpos com sucesso!');
    } catch (error) {
      console.error('Error deleting data:', error);
      // "Nada foi apagado" só pode ser dito porque a limpeza virou transação
      // única — com os cinco deletes em sequência, a frase era falsa.
      toast.error('Erro ao limpar dados. Nada foi apagado — tente novamente.');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formName.trim() || !formEmail.trim()) {
      toast.warning('Preencha todos os campos obrigatórios');
      return;
    }

    setFormSubmitting(true);

    try {
      // Create mailto link and open
      const subject = encodeURIComponent('Solicitação de exclusão de dados - Orçamigo');
      const body = encodeURIComponent(
`Solicitação de exclusão de dados

Nome completo: ${formName}
E-mail da conta: ${formEmail}
${formReason ? `Motivo: ${formReason}` : ''}

---
Solicito a exclusão de todos os dados da minha conta no aplicativo Orçamigo, mantendo apenas o acesso à conta.

Estou ciente de que:
- Esta ação é irreversível
- Todos os meus orçamentos serão excluídos
- Todos os meus clientes cadastrados serão excluídos
- Todo o meu catálogo será excluído
- Minhas empresas e configurações serão excluídas
- Minha conta continuará ativa, apenas os dados serão removidos

Data da solicitação: ${new Date().toLocaleDateString('pt-BR')}
`
      );
      
      window.location.href = `mailto:contato@orcamigo.com.br?subject=${subject}&body=${body}`;
      
      setFormSubmitted(true);
      toast.success('E-mail aberto! Envie para completar a solicitação.');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao abrir o e-mail. Tente novamente.');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Success state after deletion
  if (deleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="text-xl font-semibold">Dados excluídos</h2>
            <p className="text-muted-foreground">
              Todos os seus dados foram excluídos com sucesso. Sua conta continua ativa.
            </p>
            <p className="text-sm text-muted-foreground">
              Você pode começar a usar o Orçamigo novamente do zero.
            </p>
            <Button3D onClick={() => navigate('/dashboard')} className="w-full mt-4">
              Ir para o dashboard
            </Button3D>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form submitted state (for non-logged users)
  if (formSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <Mail className="h-16 w-16 text-primary mx-auto" />
            <h2 className="text-xl font-semibold">Solicitação enviada</h2>
            <p className="text-muted-foreground">
              Complete o envio do e-mail que foi aberto para finalizar sua solicitação.
            </p>
            <p className="text-sm text-muted-foreground">
              Nossa equipe analisará seu pedido e responderá em até 5 dias úteis.
            </p>
            <Button3D color="gray" onClick={() => navigate('/')} className="w-full mt-4">
              Voltar ao início
            </Button3D>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not logged in state - Show form
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <img 
                src="https://kizuizwbhlttcmgmllst.supabase.co/storage/v1/object/public/public-app-assets/Logo/Logo%20Original%20Sem%20Fundo%20-%20Orcamigo.png" 
                alt="Orçamigo" 
                className="h-16 w-auto mx-auto"
              />
            </div>
            <CardTitle className="flex items-center justify-center gap-2 text-amber-600">
              <Database className="h-5 w-5" />
              Solicitar exclusão de dados
            </CardTitle>
            <CardDescription>
              Orçamigo - Formulário de solicitação de exclusão de dados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-800 mb-2">📋 Como funciona:</h3>
              <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                <li>Preencha o formulário abaixo com seus dados</li>
                <li>Clique em "Enviar solicitação"</li>
                <li>Um e-mail será preparado automaticamente</li>
                <li>Envie o e-mail para nossa equipe</li>
                <li>Você receberá uma confirmação em até 5 dias úteis</li>
              </ol>
            </div>

            {/* Data deletion info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800 mb-2">📦 Dados que serão excluídos:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Todos os seus orçamentos e histórico</li>
                <li>• Todos os clientes cadastrados</li>
                <li>• Todo o catálogo de serviços/produtos</li>
                <li>• Todas as empresas cadastradas</li>
                <li>• Todas as notificações</li>
              </ul>
              <div className="mt-3 pt-3 border-t border-amber-200">
                <p className="text-sm font-medium text-green-700 mb-1">✓ O que é mantido:</p>
                <ul className="text-sm text-muted-foreground">
                  <li>• Sua conta de usuário (e-mail e senha)</li>
                  <li>• Seu perfil básico</li>
                </ul>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                <strong>Período de retenção:</strong> Os dados são excluídos imediatamente após o processamento. 
                Logs de auditoria podem ser mantidos por até 6 meses conforme exigência legal.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome completo *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Seu nome completo"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-mail da conta *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Use o mesmo e-mail cadastrado no app Orçamigo
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Motivo da exclusão (opcional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Conte-nos por que deseja limpar seus dados..."
                  value={formReason}
                  onChange={(e) => setFormReason(e.target.value)}
                  rows={3}
                />
              </div>

              <Button3D
                type="submit"
                color="red"
                className="w-full"
                disabled={formSubmitting}
              >
                {formSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Preparando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar solicitação
                  </>
                )}
              </Button3D>
            </form>

            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Se você ainda tem acesso à sua conta:
              </p>
              <Link to="/auth">
                <Button variant="outline" className="w-full">
                  <LogIn className="h-4 w-4 mr-2" />
                  Fazer login e limpar pelo App
                </Button>
              </Link>
            </div>

            <Button variant="ghost" className="w-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged in - Show deletion confirmation
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <img 
              src="https://kizuizwbhlttcmgmllst.supabase.co/storage/v1/object/public/public-app-assets/Logo/Logo%20Original%20Sem%20Fundo%20-%20Orcamigo.png" 
              alt="Orçamigo" 
              className="h-12 w-auto mx-auto mb-2"
            />
          </div>
          <div className="mx-auto mb-4 p-3 rounded-full bg-amber-100">
            <Eraser className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="text-amber-700">Limpar todos os dados</CardTitle>
          <CardDescription>
            Esta ação apagará todos os seus dados, mas manterá sua conta.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* What will be deleted */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm font-medium text-amber-800 mb-2">Serão excluídos permanentemente:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Todos os seus orçamentos e histórico</li>
              <li>• Todos os clientes cadastrados</li>
              <li>• Todo o catálogo de serviços/produtos</li>
              <li>• Todas as empresas cadastradas</li>
              <li>• Todas as notificações</li>
            </ul>
          </div>

          {/* What will be kept */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm font-medium text-green-700 mb-2">Será mantido:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Sua conta de usuário</li>
              <li>• Seu perfil básico (nome, e-mail)</li>
            </ul>
          </div>

          {/* Current account info */}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Conta logada:</p>
            <p className="font-medium">{user.email}</p>
          </div>

          {/* Confirmation */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="understood"
              checked={understood}
              onCheckedChange={(checked) => setUnderstood(checked === true)}
            />
            <label
              htmlFor="understood"
              className="text-sm text-muted-foreground leading-tight cursor-pointer"
            >
              Entendo que esta ação é irreversível e que todos os meus dados serão permanentemente excluídos.
            </label>
          </div>

          {/* Action buttons */}
          <div className="space-y-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button3D
                  color="red"
                  className="w-full"
                  disabled={!understood}
                >
                  <Eraser className="h-4 w-4 mr-2" />
                  Limpar todos os dados
                </Button3D>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Última confirmação</AlertDialogTitle>
                  <AlertDialogDescription>
                    Você está prestes a excluir todos os dados da sua conta. 
                    Sua conta continuará ativa, mas todos os orçamentos, clientes e catálogo serão apagados.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteData}
                    disabled={deleting}
                    className="bg-amber-600 text-white hover:bg-amber-700"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Limpando...
                      </>
                    ) : (
                      'Sim, limpar tudo'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button3D color="gray" className="w-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Cancelar e voltar
            </Button3D>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}